/*
 * Copyright (c) 2022. American Express Company
 */

import React from 'react';
import { Icon } from '@americanexpress/dls-react';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Typography from 'src/components/typography/Typography';
import Card from 'src/components/atomic/atoms/card/Card';
import colors from 'src/styles/colors.module.scss';
import scissorsSvg from 'src/assets/svg/scissors.svg';
import { BudgetRecapViewModel } from 'src/types/view-models/budget-recap.view.model';
import { BudgetRecapVariant } from 'src/types/view-models/BudgetRecapVariant.enum';

import styles from './budgetRecap.module.scss';
import { CardVariant } from 'src/types/card-variant.enum';

const renderVariant = (
    variant: BudgetRecapVariant,
    budgetsLength: number,
    viewBudgetsOnClick: (...args: any[]) => any
): JSX.Element => {
    switch (variant) {
        case 'underBudget':
            return (
                <Typography variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('Insights.budgetRecap.underBudget[0]')}
                    <Typography component="span" variant={TypographyVariants.BODY} className={colors.good}>
                        {LiteralsService.getLiteral('Insights.budgetRecap.underBudget[1]')}
                    </Typography>
                    {LiteralsService.getLiteral('Insights.budgetRecap.underBudget[2]')}
                </Typography>
            );
        case 'almostOverspending':
            return (
                <Typography variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('Insights.budgetRecap.almostOverspending[0]')}
                    <Typography component="span" variant={TypographyVariants.BODY} className={colors.warningStrong}>
                        {LiteralsService.getLiteral('Insights.budgetRecap.almostOverspending[1]')}
                    </Typography>
                    {LiteralsService.getLiteral('Insights.budgetRecap.almostOverspending[2]')}
                    <Anchor onClick={viewBudgetsOnClick}>
                        {budgetsLength}
                        {LiteralsService.getLiteral(
                            budgetsLength === 1 ? 'Insights.budgetRecap.budget' : 'Insights.budgetRecap.budgets'
                        )}
                    </Anchor>
                </Typography>
            );
        case 'overspending':
            return (
                <Typography variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('Insights.budgetRecap.overspending[0]')}
                    <Typography component="span" variant={TypographyVariants.BODY} className={colors.bad}>
                        {LiteralsService.getLiteral('Insights.budgetRecap.overspending[1]')}
                    </Typography>
                    {LiteralsService.getLiteral('Insights.budgetRecap.overspending[2]')}
                    <Anchor onClick={viewBudgetsOnClick}>
                        {budgetsLength}
                        {LiteralsService.getLiteral(
                            budgetsLength === 1 ? 'Insights.budgetRecap.budget' : 'Insights.budgetRecap.budgets'
                        )}
                    </Anchor>
                </Typography>
            );
    }
};

export default function BudgetRecap({
    variant,
    budgetsLength,
    viewBudgetsOnClick,
    ...props
}: BudgetRecapViewModel): JSX.Element {
    return (
        <Card row variant={CardVariant.SECONDARY} className={styles.budgetRecap} {...props}>
            <Icon>
                <img src={scissorsSvg} />
            </Icon>
            {renderVariant(variant, budgetsLength, viewBudgetsOnClick)}
            <Anchor onClick={viewBudgetsOnClick} data-cy="insights-budgets-anchor">
                {LiteralsService.getLiteral('Insights.budgetRecap.viewBudgets')}
            </Anchor>
        </Card>
    );
}
